import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List, Tooltip } from "@mui/material";
import RectangleIcon from "@mui/icons-material/Rectangle";
import moment from "moment";
import { useNavigate } from "react-router-dom";

// Interface for the props of this component
interface FineTuningRequestsTableProps {
    jobs: any[]; // Expects the ProcessedFineTuningJob structure from the parent
}

const FineTuningRequestsTable: React.FC<FineTuningRequestsTableProps> = ({
    jobs,
}: FineTuningRequestsTableProps) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    // Group statuses for styling
    const pendingStatuses = ['pending', 'submitted', 'provisioning'];
    const activeStatuses = ['active', 'approved', 'success'];
    const rejectedStatuses = ['rejected', 'failed'];

    return (
        <List sx={{ bgcolor: "#fafafa" }}>
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px" }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        <Grid item xs={2}>
                            <Typography fontSize="12px">JOB NAME</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography fontSize="12px">APPLICATION</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px">OWNER</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px">REQUESTED ON</Typography>
                        </Grid>
                        <Grid textAlign="start" item xs={2}>
                            <Typography fontSize="12px">ACTIONED BY / REASON</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px">ACTIONED ON</Typography>
                        </Grid>
                    </Grid>
                </Box>
            </ListItem>
            {jobs.map((job: any, index: number) => (
                <ListItem
                    key={job.id}
                    sx={{
                        border: cardHoverIndex === index ? "1px solid #000" : "1px solid #00000000",
                        mt: 1,
                        cursor: "pointer",
                        bgcolor: activeStatuses.includes(job.status)
                                ? "#FFFFFF"
                                : rejectedStatuses.includes(job.status)
                                ? "#F0443810"
                                : "#FDB02210", // Default to pending color
                        borderRadius: "8px",
                    }}
                    onClick={() => {
                        // Navigate to the fine-tuning request details page
                        navigate(`/aisolutions/requests/finetuning/${job.id}`);
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => {
                        setCardHoverIndex(null);
                    }}
                >
                    <Box width="100%" sx={{}}>
                        <Grid container direction="row" alignItems="center" justifyContent="space-between">
                            <Grid item xs={2}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <RectangleIcon
                                        sx={{
                                            color: activeStatuses.includes(job.status)
                                                    ? "#12B76A"
                                                    : rejectedStatuses.includes(job.status)
                                                    ? "#F04438"
                                                    : "#FDB022",
                                            bgcolor: activeStatuses.includes(job.status)
                                                    ? "#12B76A"
                                                    : rejectedStatuses.includes(job.status)
                                                    ? "#F04438"
                                                    : "#FDB022",
                                            width: "4px",
                                            marginRight: "8px",
                                        }}
                                    />
                                    <Typography fontSize="14px" fontFamily="Gilroy-Medium">
                                        {job.jobName}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                    {job.applicationName || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Avatar
                                        src={job.jobOwnerAvatar} // Will be undefined, showing fallback
                                        alt={job.jobOwnerName}
                                        sx={{ marginRight: "8px", height: 30, width: 30 }}
                                    >
                                     {/* Fallback to initials if no src */}
                                     {job.jobOwnerName ? job.jobOwnerName.charAt(0).toUpperCase() : ''}
                                    </Avatar>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                        {job.jobOwnerName || 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                        {job.createdAt ? moment(job.createdAt).format("DD/MM/YYYY | hh:mm A") : 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                {job.status !== "pending" && (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {job.jobApproverName && (
                                          <Avatar
                                                alt={job.jobApproverName}
                                                // VVVV --- MAKE THE FOLLOWING CHANGE --- VVVV
                                                src={job.jobApproverAvatar} // <-- ADD THIS LINE
                                                sx={{ marginRight: "8px", height: 30, width: 30 }}
                                            >
                                                {/* This initial will now act as a fallback */}
                                                {job.jobApproverName.charAt(0).toUpperCase()}
                                            </Avatar>
                                        )}
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" mr={1}>
                                            {job.jobApproverName || 'N/A'}
                                        </Typography>
                                        {rejectedStatuses.includes(job.status) && job.reasonForRejection && (
                                            <Tooltip title={`Reason: ${job.reasonForRejection}`}>
                                                <img src="/assets/info.svg" alt="info" />
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={2}>
                                {job.actionedAt && !pendingStatuses.includes(job.status) && (
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                            {moment(job.actionedAt).format("DD/MM/YYYY | hh:mm A")}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default FineTuningRequestsTable;